import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { cedulaValida, poli } from './poliApi';

/**
 * P-2 Nueva solicitud, individual o grupal. Una fila por persona; se puede cargar un Excel con la
 * plantilla fija. El servidor valida cada fila (cedula ecuatoriana, repetidas, pruebas abiertas)
 * antes de crear nada: si una fila falla, no se crea ninguna prueba.
 */
const FILA_VACIA = () => ({ cedula: '', nombres: '', apellidos: '', fechaIngreso: '', puestoId: null });
const COLUMNAS_PLANTILLA = ['cedula', 'nombres', 'apellidos', 'fecha_ingreso (aaaa-mm-dd)', 'puesto'];

const NuevaSolicitud = ({ visible, cfg, alCerrar, alCrear }) => {
    const archivo = useRef(null);
    const companias = (cfg && cfg.companias) || [];
    const [cab, setCab] = useState({});
    const [filas, setFilas] = useState([FILA_VACIA()]);
    const [errores, setErrores] = useState([]);
    const [mensaje, setMensaje] = useState(null);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        if (visible) {
            setCab({ companiaId: companias.length === 1 ? companias[0].compania_id : null });
            setFilas([FILA_VACIA()]);
            setErrores([]);
            setMensaje(null);
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    const compania = companias.find((c) => c.compania_id === cab.companiaId);
    const catalogo = (tipo) => ((cfg && cfg.catalogos) || []).filter((c) => c.tipo === tipo && c.activo
        && (tipo !== 'PUESTO' || c.compania_id === cab.companiaId));
    const puestos = useMemo(() => catalogo('PUESTO'), [cfg, cab.companiaId]); // eslint-disable-line react-hooks/exhaustive-deps
    const opc = (lista) => lista.map((c) => ({ label: c.nombre, value: c.catalogo_id }));
    const grupal = filas.length > 1;

    const cambiar = (i, k, v) => setFilas(filas.map((f, j) => (j === i ? { ...f, [k]: v } : f)));

    const descargarPlantilla = () => {
        const hoja = XLSX.utils.aoa_to_sheet([COLUMNAS_PLANTILLA, ['0912345675', 'Nombres', 'Apellidos', '2025-01-15', 'Nombre del puesto']]);
        const libro = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(libro, hoja, 'Personas');
        XLSX.writeFile(libro, 'Plantilla solicitud de poligrafia.xlsx');
    };

    const cargarExcel = async (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        try {
            const libro = XLSX.read(await f.arrayBuffer(), { cellDates: true });
            const datos = XLSX.utils.sheet_to_json(libro.Sheets[libro.SheetNames[0]], { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
            const nuevas = datos.slice(1).filter((r) => r && r.some((c) => String(c || '').trim())).map((r) => {
                const puesto = puestos.find((p) => p.nombre.toLowerCase() === String(r[4] || '').trim().toLowerCase());
                return {
                    cedula: String(r[0] || '').replace(/\D/g, '').padStart(10, '0').slice(-10),
                    nombres: String(r[1] || '').trim(),
                    apellidos: String(r[2] || '').trim(),
                    fechaIngreso: String(r[3] || '').trim().slice(0, 10),
                    puestoId: puesto ? puesto.catalogo_id : null
                };
            });
            if (!nuevas.length) throw new Error('El archivo no tiene filas debajo de los titulos');
            setFilas(nuevas);
            setErrores([]);
            setMensaje({ severity: 'info', text: `${nuevas.length} personas cargadas del Excel. Revise y presione Validar.` });
        } catch (err) {
            setMensaje({ severity: 'error', text: `No se pudo leer el Excel: ${err.message}` });
        }
    };

    const cuerpo = () => ({ ...cab, grupal, personas: filas });

    const validar = async () => {
        setOcupado(true);
        setMensaje(null);
        try {
            const r = await poli('/solicitudes/validar', 'POST', cuerpo());
            setErrores(r.errores || []);
            const malas = (r.errores || []).filter(Boolean).length;
            setMensaje(malas ? { severity: 'warn', text: `${malas} filas con observaciones` } : { severity: 'success', text: 'Todas las filas estan bien' });
            return malas === 0;
        } catch (e) {
            setMensaje({ severity: 'error', text: e.message });
            return false;
        } finally {
            setOcupado(false);
        }
    };

    const enviar = async () => {
        if (!(await validar())) return;
        setOcupado(true);
        try {
            const r = await poli('/solicitudes', 'POST', cuerpo());
            if (!r.creada) {
                setErrores(r.errores || []);
                setMensaje({ severity: 'warn', text: 'Hay filas con observaciones' });
                return;
            }
            alCrear(r);
        } catch (e) {
            setMensaje({ severity: 'error', text: e.message });
        } finally {
            setOcupado(false);
        }
    };

    const listo = cab.companiaId && filas.length && (!grupal || (cab.nombreGrupo || '').trim());

    return (
        <Dialog header="Nueva solicitud de poligrafia" visible={visible} style={{ width: 'min(70rem, 97vw)' }} onHide={alCerrar}
            footer={(
                <div className="flex justify-content-between flex-wrap gap-2">
                    <span className="text-600 align-self-center">{filas.length} {filas.length === 1 ? 'persona' : 'personas'} · se crea una prueba por persona en estado «Solicitada»</span>
                    <div className="flex gap-2">
                        <Button label="Validar" icon="pi pi-check-square" className="p-button-outlined" onClick={validar} loading={ocupado} disabled={!cab.companiaId} />
                        <Button label="Enviar solicitud" icon="pi pi-send" onClick={enviar} loading={ocupado} disabled={!listo} />
                    </div>
                </div>
            )}>
            <div className="grid formgrid p-fluid">
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-compania">Compania</label>
                    {companias.length === 1 ? <InputText id="sol-compania" value={companias[0].nombre} disabled />
                        : <Dropdown inputId="sol-compania" value={cab.companiaId} placeholder="Elija la compania"
                            options={companias.map((c) => ({ label: c.nombre, value: c.compania_id }))} onChange={(e) => setCab({ ...cab, companiaId: e.value, proyectoId: null })} />}
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-proyecto">Proyecto o division</label>
                    <Dropdown inputId="sol-proyecto" value={cab.proyectoId} showClear placeholder="Proyecto"
                        options={((compania && compania.proyectos) || []).map((p) => ({ label: p.nombre, value: p.proyecto_id }))} onChange={(e) => setCab({ ...cab, proyectoId: e.value })} />
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-motivo">Motivo</label>
                    <Dropdown inputId="sol-motivo" value={cab.motivoId} showClear placeholder="Motivo" options={opc(catalogo('MOTIVO'))} onChange={(e) => setCab({ ...cab, motivoId: e.value })} />
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-tipo">Tipo de prueba</label>
                    <Dropdown inputId="sol-tipo" value={cab.tipoPruebaId} showClear placeholder="Tipo" options={opc(catalogo('TIPO_PRUEBA'))} onChange={(e) => setCab({ ...cab, tipoPruebaId: e.value })} />
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-grupo">Nombre del grupo {grupal ? '(obligatorio)' : '(opcional)'}</label>
                    <InputText id="sol-grupo" value={cab.nombreGrupo || ''} placeholder="Ej.: Ingreso personal octubre" onChange={(e) => setCab({ ...cab, nombreGrupo: e.target.value })} />
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="sol-obs">Observacion</label>
                    <InputTextarea id="sol-obs" rows={1} autoResize value={cab.observacion || ''} onChange={(e) => setCab({ ...cab, observacion: e.target.value })} />
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
                <Button label="Agregar persona" icon="pi pi-user-plus" className="p-button-sm" onClick={() => setFilas([...filas, FILA_VACIA()])} />
                <Button label="Cargar Excel" icon="pi pi-file-excel" className="p-button-sm p-button-outlined" onClick={() => archivo.current.click()} disabled={!cab.companiaId} />
                <Button label="Descargar plantilla" icon="pi pi-download" className="p-button-sm p-button-text" onClick={descargarPlantilla} />
                <input ref={archivo} type="file" accept=".xlsx,.xls" hidden onChange={cargarExcel} aria-label="Archivo Excel de personas" />
            </div>
            {mensaje && <Message className="mb-2 w-full justify-content-start" severity={mensaje.severity} text={mensaje.text} />}

            <div style={{ overflowX: 'auto' }}>
                <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '52rem' }}>
                    <thead>
                        <tr className="text-left text-600">
                            <th className="p-2">#</th><th className="p-2">Cedula</th><th className="p-2">Nombres</th><th className="p-2">Apellidos</th>
                            <th className="p-2">Fecha de ingreso</th><th className="p-2">Puesto</th><th className="p-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {filas.map((f, i) => (
                            <React.Fragment key={i}>
                                <tr className="border-top-1 surface-border">
                                    <td className="p-1 text-600">{i + 1}</td>
                                    <td className="p-1">
                                        <InputText value={f.cedula} maxLength={10} keyfilter="int" aria-label={`Cedula fila ${i + 1}`}
                                            className={f.cedula.length === 10 && !cedulaValida(f.cedula) ? 'p-invalid' : ''} style={{ width: '8.5rem' }}
                                            onChange={(e) => cambiar(i, 'cedula', e.target.value)} />
                                    </td>
                                    <td className="p-1"><InputText value={f.nombres} aria-label={`Nombres fila ${i + 1}`} onChange={(e) => cambiar(i, 'nombres', e.target.value)} /></td>
                                    <td className="p-1"><InputText value={f.apellidos} aria-label={`Apellidos fila ${i + 1}`} onChange={(e) => cambiar(i, 'apellidos', e.target.value)} /></td>
                                    <td className="p-1"><InputText type="date" value={f.fechaIngreso} aria-label={`Fecha de ingreso fila ${i + 1}`} onChange={(e) => cambiar(i, 'fechaIngreso', e.target.value)} /></td>
                                    <td className="p-1">
                                        <Dropdown value={f.puestoId} showClear placeholder="Puesto" options={opc(puestos)} aria-label={`Puesto fila ${i + 1}`}
                                            onChange={(e) => cambiar(i, 'puestoId', e.value)} style={{ width: '11rem' }} />
                                    </td>
                                    <td className="p-1">
                                        <Button icon="pi pi-times" className="p-button-text p-button-danger" aria-label={`Quitar fila ${i + 1}`} disabled={filas.length === 1}
                                            onClick={() => { setFilas(filas.filter((_, j) => j !== i)); setErrores(errores.filter((_, j) => j !== i)); }} />
                                    </td>
                                </tr>
                                {errores[i] && (
                                    <tr><td /><td colSpan={6} className="px-2 pb-2 text-red-600"><i className="pi pi-exclamation-circle" /> {errores[i]}</td></tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </Dialog>
    );
};

export default NuevaSolicitud;
